import classNames from 'classnames';
import React, { useState, useCallback, JSX } from 'react';
import { ReactComponent as ShowPassword } from '@/assets/show.svg';
import { ReactComponent as HidePassword } from '@/assets/hide.svg';
import { IBoxComponent, IBoxInputComponent } from '@enuan/chat__utils';
import { cleanBoxProps } from '../../../utils/cleanBoxProps';
import { FormRow } from './FormRow';
import { useDidUpdateEffect } from '@avalenti89/react-ts-utils';
import { styled } from '@mui/material/styles';
import { fontSecondaryOblique } from 'src/theme/mixins';

type Props = IBoxComponent<
	IBoxInputComponent<JSX.IntrinsicElements['input']>
> & {
	onChange?: (value: string | undefined) => void;
};

export const Input = styled(
	({
		// custom props
		type,
		help_label,
		error,
		error_label,
		required_label,
		label,
		inputType = 'text',
		onChange,

		// standard props
		id, //ignore it to avoid duplicate
		value = '',
		className,
		disabled,
		required,
		..._props
	}: Props) => {
		const props = cleanBoxProps(_props);

		const [showPassword, setShowPassword] = useState(inputType !== 'password');

		const togglePassword = useCallback(() => {
			if (!disabled) {
				setShowPassword((state) => !state);
			}
		}, [disabled]);
		useDidUpdateEffect(() => {
			// Restore hidden password when disabled
			setShowPassword(false);
		}, [disabled]);

		const _onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
			({ target: { value } }) => {
				onChange?.(value);
			},
			[onChange],
		);

		const showPasswordToggler = inputType === 'password' && !disabled;

		const _type = inputType === 'password' && showPassword ? 'text' : inputType;

		return (
			<FormRow
				className={classNames('form__row--input', className)}
				label={label}
				error={error}
				error_label={error_label}
				help_label={help_label}
				required={required}
				required_label={required_label}
			>
				<div className="input-group">
					<input
						type={_type || 'text'}
						value={value}
						className={classNames(
							'input',
							!!error && 'input--error',
							`input--${inputType}`,
							!!value && `input--compiled`,
						)}
						onChange={_onChange}
						autoComplete={!showPassword ? 'current-password' : undefined}
						disabled={disabled}
						required={required}
						{...props}
					/>
					{showPasswordToggler && (
						<>
							{!showPassword ? (
								<ShowPassword
									className={classNames('icon', 'icon--show')}
									onClick={() => togglePassword()}
								/>
							) : (
								<HidePassword
									className={classNames('icon', 'icon--hide')}
									onClick={() => togglePassword()}
								/>
							)}
						</>
					)}
				</div>
			</FormRow>
		);
	},
	{
		target: 'Input-root',
		label: 'Input',
	},
)({
	'& .input': {
		...fontSecondaryOblique,
		borderWidth: '1px',
		borderColor: 'transparent',

		borderBottomColor: 'var(--form__element_border-color)',
		borderRadius: '0.4em 0.4em 0',
		padding: '0.2em 0.4em',
		outline: 'none',
		width: '100%',
		boxSizing: 'border-box',
		fontSize: '1.5em',
		color: 'var(--form__element_color)',
		backgroundColor: 'var(--form__element_background)',

		'&::placeholder': {
			color: 'var(--form__element__placeholder_color)',
		},
		'&.input--password': {
			paddingRight: '2em',
		},
	},

	'& .input-group': {
		'& .icon': {
			cursor: 'pointer',
			marginRight: '0.8em',
			width: '2em',
			height: '2em',
			display: 'flex',
			alignItems: 'center',
			transition: 'all 150ms',
		},
		'& .input + .icon': {
			position: 'absolute',
			right: 0,
			top: '50%',
			transform: 'translateY(-50%)',
			zIndex: 10,
			fill: 'var(--form__input__icon_color)',
			stroke: 'transparent',
			// TODO check why it seems not used
			'&.icon--disabled': {
				cursor: 'not-allowed',
			},
		},
		'& .input--datetime, & .input--date': {
			backgroundColor: 'var(--form__date_background)',
			borderBottom: 0,
			border: 0,
			cursor: 'text',
			borderRadius: '0.4em 0.4em 0 0.4em',
			padding: '0.2em 0.4em',
			minHeight: '2.4em',
			position: 'relative',

			'&::-webkit-datetime-edit-month-field, &::-webkit-datetime-edit-day-field, &::-webkit-datetime-edit-year-field, &::-webkit-datetime-edit-text':
				{
					color: 'var(--form__date_placeholder)',
				},

			'&.input--compiled': {
				'&::-webkit-datetime-edit-month-field, &::-webkit-datetime-edit-day-field, &::-webkit-datetime-edit-year-field, &::-webkit-datetime-edit-text':
					{
						color: 'var(--form__date_color)',
					},
			},
		},
	},
	'& .form__label': {
		marginBottom: '0.2em',
		fontSize: '1.3em',
	},
	'& .input--error': {
		color: 'var(--form__element--error_color)',
		borderBottomColor: 'var(--form__element--error_color)',

		'&::placeholder': {
			color: 'var(--form__element--error__placeholder_color)',
		},
	},
});
